import React, { useState, useEffect } from 'react';
import {
	Grid,
	Button,
	XFilledIcon,
	Modal,
	Alert,
} from 'react-unity';
import Organization from '../../../models/entities/Organization';
import { AlertBanner } from '../../../models/interfaces/AlertBanner';
import SubscriptionRequestsService from '../../../services/SubscriptionRequestsService';
import SubscriptionMultiEditModal from './SubscriptionMultiEditModal';
import { getOrganizations } from '../../common/OrganizationStorage';
import SubscriptionRequest from '../../../models/entities/SubscriptionRequest';
import { authenticatedUser } from '../../../authentication/authModule';

export interface ISubscriptionEditMultipleProps {
	visible: boolean;
	onCancel: (e) => any;
	onClose: (e) => any;
	onConfirm: (e) => any;
	mySubscriptions: SubscriptionRequest[];
	setCheckBoxState: any;
	setAlert: any;
}

const SubscriptionsEditModal = ({ visible, onCancel, onClose, onConfirm, mySubscriptions, setCheckBoxState, setAlert }: ISubscriptionEditMultipleProps) => {

	const [submitting, setSubmitting] = useState(false);
	const [organizationsList, setOrganizationList] = useState<Organization[]>([]);
	const [allOrganizationsList, setAllOrganizationList] = useState<Organization[]>([]);
	const [subscriptionsService] = useState<SubscriptionRequestsService>(new SubscriptionRequestsService())
	const [loadingAlert, setLoadingAlert] = useState<AlertBanner>({
		visible: false,
		variant: 'error',
		text: ''
	});

	const handleCancel = (e: any) => {
		resetStateForm();
		onClose(e);
	};

	const handleClose = (e: any) => {
		resetStateForm();
		onCancel(e);
	};

	const handleRequest = async (e: any) => {
		window.scroll(0, 0);
		setSubmitting(true);
		setLoadingAlert({
			visible: true,
			text: 'Saving changes... This could take several minutes'
		});
		const subsSelected = mySubscriptions.filter((sub) => sub.selected);

		const subsToEdit = subsSelected.map((sub) => {
			const temp = {
				subscriptionId: sub.id,
				selectedL3GlobalId: form.organizationL3.value || sub.organization.L3.displayName,
				organizationGlobalId: form.organizationL5.value || form.organizationL4.value || sub.organization.L4.displayName,
				environment: sub.environment.value,
				platformManagementGroup: sub.platformManagementGroup,
				serviceEnvironment: sub.serviceEnvironment,
				cartaIds: sub.cartaIds,
				isProject: sub.isProject,
				wpmCode: parseInt(sub.wpmCode),
				closeOutDate: sub.closeOutDate,
				isConnected: sub.isConnected,
				amountOfDevices: null,
				region: sub.region,
				contributorAccessGroupId: '',
				initialResourceGroup: sub.resourceGroupName,
				ownerUserId: form.owner['id'] || sub.owner.id,
				custodianUserId: form.custodian['id'] || sub.custodian.id,
				custodianTwoUserId: form.custodianTwo['id'] || sub.custodianTwo?.id,
				subscriptionUsedFor: sub.subscriptionUsedFor,
			};
			return temp;
		});
		subscriptionsService.updateMultiple(subsToEdit)
			.then(() => {
				setAlert({
					visible: true,
					variant: 'success',
					text: 'Subscriptions updated successfully.'
				});
				setSubmitting(false);
				resetStateForm();
				onConfirm(e);
			})
			.catch((err) => {
				if (err.status != 0) {
					setAlert({
						visible: true,
						variant: 'error',
						// text: err?.response?.data?.message || `The subscription ${err.request?.body?.subscriptionId} could not be updated. Please, try again later.`
						text: err?.status == 403 && (err?.response.headers.get("content-type") == "application/json" ? JSON.stringify(err?.response.data).includes("Application-Gateway") : err?.response?.data?.includes("Application-Gateway")) ? "The content you input contains some sensitive information. Please email to GSC-EMIT-ITOPS-HP-PLATFORMS-AZURE-CHINA.UG@exxonmobil.com and provide the UTC timestamp [" + new Date().toISOString() + "]. We will get back to you during our working hours (8:00 - 16:00 UTC+8:00)." : err?.response?.data?.message || `The subscription ${err.request?.body?.subscriptionId} could not be updated. Please, try again later.`
					});
					setTimeout(() => {
						setAlert({
							visible: false,
						});
					}, 5000);
					setSubmitting(false);
					handleClose(e);
				} else {
					setAlert({
						visible: true,
						variant: 'success',
						text: 'Subscriptions updated successfully.'
					});
					resetStateForm();
					onConfirm(e);
				}
			});
	};

	const [form, setForm] = useState({
		organizationL3: {
			value: '',
			onChange: (value) => {
				handleL3Change(value)
			}
		},
		organizationL4: {
			value: '',
			onChange: (value) => {
				handleL4Change(value)
			}
		},
		organizationL5: {
			value: '',
			onChange: (value) => {
				handleL5Change(value)
			}
		},
		globalOrganization: {
			value: '',
			onChange: (value) => {
				handleGlobalOrganizationChange(value)
			}
		},
		owner: {
			value: '',
			onChange: (value) => {
				handleInput('owner', value)
			}
		},
		custodian: {
			value: '',
			onChange: (value) => {
				handleInput('custodian', value)
			}
		},
		custodianTwo: {
			value: '',
			onChange: (value) => {
				handleInput('custodianTwo', value)
			}
		}
	});

	const stateFormHandler = () => getStateFormHandlers();

	const getStateFormHandlers = () => {
		return {
			owner: {
				value: '',
				onChange: (value) => {
					handleInput('owner', value)
				}
			},
			custodian: {
				value: '',
				onChange: (value) => {
					handleInput('custodian', value)
				}
			},
			custodianTwo: {
				value: '',
				onChange: (value) => {
					handleInput('custodianTwo', value)
				}
			}
		}
	}

	const handleL3Change = (event) => {
		handleInput('organizationL3', event.target.value);
		form.globalOrganization.onChange(null);
	};

	const handleL4Change = (event) => {
		handleInput('organizationL4', event.target.value);
		form.globalOrganization.onChange(event.target.value);
	};

	const handleL5Change = (event) => {
		handleInput('organizationL5', event.target.value);
		form.globalOrganization.onChange(event.target.value);
	};

	const handleGlobalOrganizationChange = (value) => {
		setForm({ ...form, globalOrganization: { ...form.globalOrganization, value: value } });
	};

	const organizationOptions = (parent: string | null) => {
		return organizationsList
			.filter((organization) => organization.findParent(organizationsList)?.displayName == parent && organization.status == 'Active')
			.map((organization) => ({ value: organization.displayName, text: organization.displayName })).sort((a, b) => a.value > b.value ? 1 : -1);
	};

	const organizationOptionsL5 = (parent: string | null) => {
		var options = organizationOptions(parent);
		options.unshift({ value: null, text: '' });
		return options;
	};


	const handleInput = async (fieldName, value) => {
		form[fieldName] = value;
		setForm(form);
	}

	const resetStateForm = () => {
		setForm({
			...form,
			organizationL3: { ...form.organizationL3, value: '' },
			organizationL4: { ...form.organizationL4, value: '' },
			organizationL5: { ...form.organizationL5, value: '' },
			globalOrganization: { ...form.globalOrganization, value: '' },
			owner: { ...form.owner, value: '' },
			custodian: { ...form.custodian, value: '' },
			custodianTwo: { ...form.custodianTwo, value: '' }
		});
		mySubscriptions.map((sub) => sub.selected = false);
		setCheckBoxState(0);
	};

	useEffect(() => {
		const fetchData = async () => {
			let organizations = (await getOrganizations()).map(o => new Organization(o));
			setAllOrganizationList(organizations);
			setOrganizationList(organizations);
		}
		fetchData();
	}, []);

	useEffect(() => {
		setOrganizationList(allOrganizationsList);
	}, []);

	return (
		<Modal show={visible}>
			<Modal.Window className="autoOverflowModal" id="subs-multi-edit-modal">
				<Modal.Window.Header>
					<Modal.Window.Header.Title>Edit Your Subscriptions Selected</Modal.Window.Header.Title>
					<Modal.Window.Header.CloseButton onClick={onClose}>
						<XFilledIcon size='small' />
					</Modal.Window.Header.CloseButton>
				</Modal.Window.Header>
				{loadingAlert.visible &&
					<Alert
						variant={loadingAlert.variant}
						onClose={() => {
							setAlert({
								visible: false,
							});
						}}
					>
						{loadingAlert.text}
					</Alert>}
				<Modal.Window.Body>
					<SubscriptionMultiEditModal
						form={form}
						organizationOptions={organizationOptions}
						organizationOptionsL5={organizationOptionsL5}
						handleL3Change={(e) => setForm({ ...form, organizationL3: { ...form.organizationL3, value: e.target.value } })}
						handleL4Change={(e) => {
							setForm({ ...form, globalOrganization: { ...form.globalOrganization, value: e.target.value } });
							setForm({ ...form, organizationL4: { ...form.organizationL4, value: e.target.value } });
						}}
						handleL5Change={(e) => {
							setForm({ ...form, globalOrganization: { ...form.globalOrganization, value: e.target.value } });
							setForm({ ...form, organizationL5: { ...form.organizationL5, value: e.target.value } });
						}}
						stateFormHandler={stateFormHandler}
					/>
				</Modal.Window.Body>
				<Modal.Window.Footer>
					<Grid variant="2-up">
						<Grid.Item>
							<Button
								variant="secondary"
								disabled={submitting}
								onClick={handleCancel}
							>
								Cancel
							</Button>
						</Grid.Item>
						<Grid.Item>
							<Button
								variant="primary"
								disabled={submitting}
								onClick={handleRequest}
							>
								Save
							</Button>
						</Grid.Item>
					</Grid>
				</Modal.Window.Footer>
			</Modal.Window>
		</Modal>
	);
};

export default SubscriptionsEditModal;
